import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

/**
 * Light / dark / system.
 *
 * The design system's tokens are **dark-first**: bare `:root` carries the dark
 * palette and `:root[data-theme="light"]` overrides it. That leaves no third
 * state for "follow the OS" -- with the attribute absent, a light-preferring
 * machine would get the dark palette.
 *
 * Rather than duplicate ~70 light values under a `prefers-color-scheme` query
 * (which would have to be kept in step with the design system by hand, forever),
 * this provider RESOLVES "system" itself and always writes an explicit
 * `data-theme`. `tokens/colors.css` therefore stays byte-identical to the
 * design system's copy, and a future update to it is a re-copy rather than a
 * merge.
 *
 * The matching pre-paint script in index.html does the same resolution
 * synchronously, so the first frame is already correct.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'brainspot:theme'

interface ThemeContextValue {
  /** What the user chose. */
  preference: ThemePreference
  /** What is actually on screen right now. */
  resolved: ResolvedTheme
  setPreference: (next: ThemePreference) => void
  /** Light <-> dark. From "system", flips away from whatever is showing. */
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const LIGHT_QUERY = '(prefers-color-scheme: light)'

function systemTheme(): ResolvedTheme {
  return globalThis.matchMedia?.(LIGHT_QUERY).matches ? 'light' : 'dark'
}

function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw
  } catch {
    // Private mode, or storage disabled. The default is the brand's anyway.
  }
  return 'system'
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === 'system' ? systemTheme() : preference
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference)
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(readStoredPreference()))

  // Apply, and follow the OS while the preference is "system".
  useEffect(() => {
    const apply = () => {
      const next = resolveTheme(preference)
      setResolved(next)
      document.documentElement.setAttribute('data-theme', next)
    }
    apply()

    if (preference !== 'system') return
    const mql = globalThis.matchMedia?.(LIGHT_QUERY)
    if (!mql) return
    mql.addEventListener('change', apply)
    return () => mql.removeEventListener('change', apply)
  }, [preference])

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next)
    } catch {
      // Not fatal: the theme still applies for this session.
    }
  }, [])

  // From "system", flip away from what is currently showing rather than to a
  // fixed value -- otherwise the first press can appear to do nothing.
  const toggle = useCallback(() => {
    setPreference(resolveTheme(preference) === 'dark' ? 'light' : 'dark')
  }, [preference, setPreference])

  const value = useMemo(
    () => ({ preference, resolved, setPreference, toggle }),
    [preference, resolved, setPreference, toggle],
  )

  return <ThemeContext value={value}>{children}</ThemeContext>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside a <ThemeProvider>')
  return ctx
}
